// Docs: IMPLEMENTATION.md §3.6 (Analytics). Update when adding new tracked
// flows or changing how identity/session recording is configured.
//
// Product analytics (PostHog): which flows are actually used, and session
// replay for spotting where users get stuck. Deliberately *not* full
// autocapture — we track a curated set of named "key flow" events instead
// of every click, so the data stays legible enough to justify deprecating
// or relocating a feature. Silently disabled (matches the Sentry pattern in
// index.jsx and the KI-Nutzung "no key configured" pattern) when
// VITE_POSTHOG_KEY isn't set, so this is opt-in per deployment.
import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
// This project lives on PostHog's EU cloud region; override via env var for
// a different region/self-hosted instance.
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';

let enabled = false;

export const initAnalytics = () => {
  if (!POSTHOG_KEY || enabled) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // We call capture() explicitly for the flows we care about.
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: true,
    // Session replay is the point of #2 (spotting problematic scenarios),
    // but lesson answers and API keys must never end up in a recording.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-analytics-mask]',
    },
    persistence: 'localStorage+cookie',
  });
  enabled = true;
};

// Call once the JWT is known (login, or app load with an existing token).
// user_id is the JWT `sub` claim (see backend/app/auth.py create_access_token).
export const identifyUser = (userId) => {
  if (!enabled || !userId) return;
  posthog.identify(String(userId));
};

// Decodes the `sub` claim straight out of the JWT payload (base64url, no
// signature check — this only ever runs against a token this browser was
// handed by our own backend, purely to label analytics events) and
// identifies the PostHog user. No-op if the token is missing or malformed.
export const identifyFromToken = (token) => {
  if (!enabled || !token) return;
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    const { sub } = JSON.parse(json);
    identifyUser(sub);
  } catch {
    // Malformed/expired token — the 401 interceptor will handle that separately.
  }
};

// Call on logout so the next login on this device doesn't inherit the
// previous user's identity/session.
export const resetAnalytics = () => {
  startedLessonIds.clear();
  playedPodcastIds.clear();
  playedAudioKeys.clear();
  shownCueQuestionIds.clear();
  if (!enabled) return;
  posthog.reset();
};

export const trackPageview = (path) => {
  if (!enabled) return;
  posthog.capture('$pageview', path ? { $current_url: path } : undefined);
};

// `lesson_started` means "the user began (or resumed) answering questions in
// this lesson", regardless of how the lesson came to exist — daily plan,
// manual creation, follow-up, or drill. It is fired from the one choke point
// every entry path funnels through (UnifiedDrillView's lesson fetch), and
// deduped here per lesson per page load because that view remounts on every
// exit-and-continue within a session.
const startedLessonIds = new Set();

export const trackLessonStarted = (lesson) => {
  const id = lesson?.id || lesson?.lesson_id;
  // Re-opening an already-completed lesson is reviewing, not starting.
  if (!id || lesson.status === 'completed' || startedLessonIds.has(id)) return;
  startedLessonIds.add(id);
  trackEvent('lesson_started', {
    lesson_type: lesson.lesson_type ?? null,
    source: lesson.source ?? null,
    // A non-null drill_config does NOT mean "drill": plan-day dialogue
    // lessons carry one too (the generator writes the persona/setting into
    // drill_config.dialogue — backend/app/tasks.py). `drill_type` is the
    // real discriminator, and it is also what the `/complete` response
    // echoes for `lesson_completed`, so the two events agree.
    is_drill: (lesson.drill_config?.drill_type ?? null) != null,
    drill_type: lesson.drill_config?.drill_type ?? null,
    resumed: (lesson.correct_answers || 0) + (lesson.incorrect_answers || 0) > 0,
  });
};

// ── Feature-usage events ────────────────────────────────────────────────
// Each of these has a page-load-scoped dedupe set, because the thing being
// observed (a card mounting, a question becoming current, an audio element
// starting) can repeat on re-render or on an autoplay chain, and a feature
// that looks 30× more used than it is defeats the point of the curated list.
// The sets are cleared on logout alongside the PostHog identity.

const playedPodcastIds = new Set();
const playedAudioKeys = new Set();
const shownCueQuestionIds = new Set();

// `podcast_played` — an episode actually started producing sound, as opposed
// to being generated (`podcast_generated`) or merely listed. Deduped per
// episode per page load, so pause/resume and seeking count as one listen.
export const trackPodcastPlayed = (podcast) => {
  const id = typeof podcast === 'object' && podcast !== null ? podcast.id : podcast;
  if (!id || playedPodcastIds.has(id)) return;
  playedPodcastIds.add(id);
  trackEvent('podcast_played', {
    podcast_id: String(id),
    preset: podcast?.preset ?? null,
    topic: podcast?.topic ?? null,
  });
};

export const trackPodcastTranscriptOpened = (podcast) => {
  const id = typeof podcast === 'object' && podcast !== null ? podcast.id : podcast;
  if (!id) return;
  trackEvent('podcast_transcript_opened', {
    podcast_id: String(id),
    preset: podcast?.preset ?? null,
  });
};

// `word_card_opened` — fired from WordCard's own mount effect (keyed by word
// id) rather than from the dozen places that can open one. `via` is the
// `source` prop the opening surface passes; a card rendered inline as part of
// a list passes none and is deliberately not counted as an "open".
export const trackWordCardOpened = (wordId, via) => {
  if (!wordId || !via) return;
  trackEvent('word_card_opened', { word_id: String(wordId), via });
};

// audioManager's `audioType` is an internal routing tag; these are the
// product-level kinds the dashboard breaks playback down by.
const AUDIO_KIND_BY_TYPE = {
  word: 'word',
  meaning: 'question_meaning',
  question: 'question_prompt',
};

// `audio_played` — one event per (kind, subject, autoplay) per page load.
// Without the dedupe an autoplay chain through a 20-question lesson plus the
// user's own replays would bury every other event in the project.
export const trackAudioPlayed = ({ audioType, kind, id, autoplay = false } = {}) => {
  const resolved = kind || AUDIO_KIND_BY_TYPE[audioType] || audioType || 'unknown';
  const isAutoplay = autoplay === true;
  const key = `${resolved}|${id ?? ''}|${isAutoplay ? 1 : 0}`;
  if (playedAudioKeys.has(key)) return;
  playedAudioKeys.add(key);
  trackEvent('audio_played', { kind: resolved, autoplay: isAutoplay });
};

// `cue_shown` — which cue families actually reach the user. Fired once per
// question id (a question can be revisited via the question strip), and only
// for questions that carry a cue at all.
export const trackCueShown = ({ questionId, cueKind, lessonType } = {}) => {
  if (!questionId || !cueKind || shownCueQuestionIds.has(questionId)) return;
  shownCueQuestionIds.add(questionId);
  trackEvent('cue_shown', { cue_kind: cueKind, lesson_type: lessonType ?? null });
};

// Fire-and-forget: never throw into a click handler over an analytics hiccup.
export const trackEvent = (name, properties) => {
  if (!enabled) return;
  try {
    posthog.capture(name, properties);
  } catch {
    // Analytics must never break the feature it's observing.
  }
};

export default posthog;
